import type { McqQuestion } from '../../../fetchSurvey/types/mcq';
import type { SurveyAnswers } from '../../../submitSurvey/types';
import type { LogicContext } from '../../types';
import { getVisibleMcqOptions } from '../../visibility';
import {
  buildMcqOptionLevelKey,
  isMcqOtherOptionSelected,
} from '../../fields/mcqOtherUtils';

export function sanitizeMcqAnswer(
  question: McqQuestion,
  answer: SurveyAnswers[string] | undefined,
  visibleOptionIds: Set<string>
): SurveyAnswers[string] | undefined {
  if (answer === undefined) {
    return undefined;
  }

  if (question.selectionMode === 'multiple') {
    if (!Array.isArray(answer)) {
      return answer;
    }
    const filtered = answer.filter(
      (id): id is string => typeof id === 'string' && visibleOptionIds.has(id)
    );
    return filtered.length > 0 ? filtered : undefined;
  }

  if (typeof answer === 'string' && !visibleOptionIds.has(answer)) {
    return undefined;
  }

  return answer;
}

export function sanitizeMcqOtherTextAnswers(
  question: McqQuestion,
  answers: SurveyAnswers,
  selection: SurveyAnswers[string] | undefined,
  visibleOptionIds: Set<string>
): SurveyAnswers {
  let next = { ...answers };

  for (const option of question.options) {
    if (!option.otherOption) {
      continue;
    }

    const optionLevelKey = buildMcqOptionLevelKey(question.id, option.id);
    const isVisible = visibleOptionIds.has(option.id);
    const isSelected = isMcqOtherOptionSelected(question, selection, option.id);

    if (!isVisible || !isSelected) {
      if (next[optionLevelKey] !== undefined) {
        const { [optionLevelKey]: _, ...rest } = next;
        next = rest;
      }
    }
  }

  return next;
}

export function sanitizeMcqQuestionAnswer(
  question: McqQuestion,
  answer: SurveyAnswers[string] | undefined,
  context: LogicContext
): SurveyAnswers[string] | undefined {
  const visibleOptions = getVisibleMcqOptions(question, context);
  if (question.options.length === 0 || visibleOptions.length === question.options.length) {
    return sanitizeMcqAnswer(
      question,
      answer,
      new Set(visibleOptions.map(option => option.id))
    );
  }
  return sanitizeMcqAnswer(
    question,
    answer,
    new Set(visibleOptions.map(option => option.id))
  );
}
